import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { MousePointerClick } from "lucide-react";
import { fetchGroupedCalls } from "@/lib/api";
import { useNumbers } from "@/context/NumberContext";
import { GroupedCallList } from "@/components/CallList";
import { GroupedCallCards } from "@/components/CallCards";
import { NumberView } from "@/components/NumberView";

const FILTERS = [
  { key: "tous", label: "Tous" },
  { key: "inbound", label: "Entrants" },
  { key: "outbound", label: "Sortants" },
  { key: "missed", label: "Manqués" },
];

const isDesktopViewport = () =>
  typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches;

const EmptyPanel = () => (
  <div data-testid="detail-empty" className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center flex flex-col items-center">
    <span className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
      <MousePointerClick className="h-6 w-6 text-slate-400" />
    </span>
    <p className="font-heading font-semibold text-slate-800">Aucun numéro sélectionné</p>
    <p className="text-sm text-slate-400 mt-1 max-w-[220px]">Sélectionnez un numéro pour afficher l'historique des appels.</p>
  </div>
);

const Calls = () => {
  const { selectedId, selectedNumber } = useNumbers();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("tous");
  const { data, isLoading } = useQuery({
    queryKey: ["calls-grouped", selectedId, filter],
    queryFn: () => fetchGroupedCalls(selectedId, filter),
    enabled: !!selectedId,
    refetchInterval: 30000,
    refetchOnWindowFocus: true,
  });
  const rows = data?.rows ?? [];
  const [selectedKey, setSelectedKey] = useState(null);

  // Desktop only: keep a valid selection so the side panel always has content.
  useEffect(() => {
    if (isDesktopViewport() && rows.length && !rows.some((r) => r.number_key === selectedKey)) {
      setSelectedKey(rows[0].number_key);
    }
  }, [rows]); // eslint-disable-line

  const openRow = (row) => {
    // Desktop: side panel. Mobile/tablet: full-screen unified number page.
    if (isDesktopViewport()) {
      setSelectedKey(row.number_key);
    } else {
      navigate(`/contacts/${row.number_key}`);
    }
  };

  return (
    <div data-testid="calls-page" className="space-y-6">
      <header>
        <h1 className="font-heading font-bold text-slate-900 text-2xl sm:text-3xl tracking-tight">Appels récents</h1>
        <p className="hidden md:block text-sm text-slate-500 mt-1">
          {selectedNumber ? `Historique des appels du numéro ${selectedNumber.number}.` : "Historique des appels des deux lignes."}
        </p>
      </header>

      {/* Mobile: compact segmented control (4 equal segments, single line) */}
      <div className="md:hidden grid grid-cols-4 gap-1 p-1 rounded-lg bg-slate-100 border border-slate-200" data-testid="call-filters-mobile">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`filter-m-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`text-xs font-medium py-1.5 rounded-md truncate transition-colors ${
              filter === f.key ? "bg-white text-emerald-700 shadow-sm" : "text-slate-500 active:bg-slate-200"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Desktop/tablet: pills */}
      <div className="hidden md:flex flex-wrap gap-2" data-testid="call-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium border transition-colors ${
              filter === f.key ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-7">
          {isLoading ? (
            <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-400">Chargement des appels…</div>
          ) : rows.length ? (
            <>
              <div className="hidden md:block">
                <GroupedCallList rows={rows} activeKey={selectedKey} onSelect={openRow} />
              </div>
              <div className="md:hidden">
                <GroupedCallCards rows={rows} onSelect={openRow} />
              </div>
            </>
          ) : (
            <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-400">Aucun appel pour ce filtre.</div>
          )}
        </div>
        <div className="hidden lg:block lg:col-span-5">
          <div className="lg:sticky lg:top-8 max-h-[calc(100vh-6rem)] overflow-y-auto pr-1">
            {selectedKey ? (
              <NumberView key={selectedKey} numberKey={selectedKey} onDeleted={() => setSelectedKey(null)} />
            ) : (
              <EmptyPanel />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calls;
